import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { SCOPES } from '@/features/config/scopes';
import { SCOPE_TABS } from './scopes';

/**
 * The scope the client config page shows, kept in the URL: ?tab=default|ledger|brokerGroup
 * and, for a ledger or broker group, &id=<ledger or broker group id>. Returns
 * { scope: { scope, scopeId }, changeScope({ scope, scopeId }) }; scope is stable between
 * renders while the URL is unchanged. changeScope keeps the URL's other params (e.g. the
 * grid's ?search=).
 */
export const useFormFieldConfigScope = () => {
	const [searchParams, setSearchParams] = useSearchParams();
	const tab =
		SCOPE_TABS.find(({ value }) => value === searchParams.get('tab'))?.value ?? SCOPES.DEFAULT;
	const id = searchParams.get('id');

	const scope = useMemo(
		() => ({ scope: tab, scopeId: tab === SCOPES.DEFAULT ? null : id }),
		[tab, id],
	);

	const changeScope = useCallback(
		(next) =>
			setSearchParams(
				(previous) => {
					const params = new URLSearchParams(previous);
					params.set('tab', next.scope);
					if (next.scopeId) {
						params.set('id', next.scopeId);
					} else {
						params.delete('id');
					}
					return params;
				},
				{ replace: true },
			),
		[setSearchParams],
	);

	return { scope, changeScope };
};
